// Types
import { TDealerInventoryTrade } from "../types";
import { TActionToDo, TUnprocessedVin } from "../types/dealerTrades";
import { GridRowId } from "@mui/x-data-grid-premium";

// Constants
import { APPROVE, CANCEL, CONFIRM_DECLINE, REJECT, STATUS_REQUEST, TRANSFER_ASSET_SWAP } from "../constants";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

function renderString(value?: string | null) {
	return value ?? "";
}

function determineAction({
	requestSelected,
	locationId,
}: {
	requestSelected: TDealerInventoryTrade | null;
	locationId: string;
}): TActionToDo {
	if (!requestSelected) {
		return "";
	}

	if (isDeletable({ requestSelected, locationId })) {
		return CANCEL;
	} else if (isRejectable({ requestSelected, locationId })) {
		return REJECT;
	} else if (isApprovable({ requestSelected, locationId })) {
		return APPROVE;
	} else if (isConfirmable({ requestSelected, locationId })) {
		return CONFIRM_DECLINE;
	}

	return "";
}

function actionEditAssetSwap({
	rowsSelected,
	rows = [],
	currentLocation,
}: {
	rowsSelected: GridRowId[];
	rows: TDealerInventoryTrade[];
	currentLocation: string;
}) {
	const defaultValue = {
		hideButton: true,
		disableButton: true,
		itemSelected: {} as TDealerInventoryTrade,
		itemToSwap: {} as TDealerInventoryTrade,
	};

	if (!rowsSelected.length) {
		return defaultValue;
	}

	const [rowSelected] = rowsSelected;
	const itemSelected = rows.find((item: TDealerInventoryTrade) => item._id === rowSelected);

	if (!itemSelected) {
		return defaultValue;
	}

	defaultValue.itemSelected = itemSelected;

	defaultValue.hideButton = itemSelected.transferType !== TRANSFER_ASSET_SWAP;

	defaultValue.disableButton =
		itemSelected.requestStatus.status !== STATUS_REQUEST.PENDING ||
		rowsSelected.length > 1 ||
		itemSelected.requestedByLocationId === currentLocation;

	defaultValue.itemToSwap =
		rows.find((item: TDealerInventoryTrade) => item.swapId === itemSelected.swapId && item._id !== itemSelected._id) ??
		({} as TDealerInventoryTrade);

	return defaultValue;
}

function isConfirmable({ requestSelected, locationId }: { requestSelected?: TDealerInventoryTrade; locationId: string }) {
	return (
		requestSelected?.requestedByLocationId === locationId &&
		requestSelected?.requestStatus.status === STATUS_REQUEST.AVAILABLE_FOR_TRADE
	);
}

function isApprovable({ requestSelected, locationId }: { requestSelected?: TDealerInventoryTrade; locationId: string }) {
	if (!requestSelected || requestSelected?.requestedByLocationId === locationId) {
		return false;
	}

	return or(
		requestSelected.requestStatus.status === STATUS_REQUEST.PENDING,
		requestSelected.requestStatus.status === STATUS_REQUEST.PENDING_ARRIVAL_APPROVAL
	);
}

function isRejectable({ requestSelected, locationId }: { requestSelected?: TDealerInventoryTrade; locationId: string }) {
	if (!requestSelected || requestSelected?.requestedByLocationId === locationId) {
		return false;
	}

	// A trade is Rejectable if it is Pending or Pending Arrival and not blocked
	return and(
		or(
			requestSelected.requestStatus.status === STATUS_REQUEST.PENDING,
			requestSelected.requestStatus.status === STATUS_REQUEST.PENDING_ARRIVAL
		),
		!requestSelected.isBlocked
	);
}

function isDeletable({ requestSelected, locationId }: { requestSelected?: TDealerInventoryTrade; locationId: string }) {
	if (!requestSelected || requestSelected?.requestedByLocationId !== locationId) {
		return false;
	}

	const isPendingApproval = requestSelected.requestStatus.status === STATUS_REQUEST.PENDING;
	const isPendingArrivalApproval = requestSelected.requestStatus.status === STATUS_REQUEST.PENDING_ARRIVAL_APPROVAL;

	// A trade is Deletable if it is (Pending Approval or Pending Arrival Approval) and not blocked
	return or(isPendingApproval, isPendingArrivalApproval);
}

function isDealerTradeBlocked({ requestSelected }: { requestSelected?: TDealerInventoryTrade; locationId: string }) {
	if (!requestSelected) {
		return false;
	}
	return requestSelected.isBlocked;
}

function getUnprocessedVins(transferRequests: TDealerInventoryTrade[] = []) {
	return transferRequests.reduce<TUnprocessedVin[]>((acc, { requestId, vehicle, swapId, isBlocked }, _, arr) => {
		if (!isBlocked) {
			return acc;
		}

		if (!swapId && requestId) {
			return [...acc, { vin: vehicle.vinNumber }];
		}

		/* Verify if the current block trade was already added */
		const currentSwap = acc.find((accItem) =>
			[accItem?.swap?.vin ?? "", accItem?.swap?.partner?.vin ?? ""].includes(vehicle.vinNumber)
		);

		if (currentSwap) {
			return acc;
		}

		/* Define the vinA */
		const partner = arr.find((arrItem) => arrItem.swapId === swapId && arrItem.requestId !== requestId);

		partner &&
			acc.push({ swap: { vin: vehicle.vinNumber, partner: { vin: partner.vehicle.vinNumber, isBlocked: partner.isBlocked } } });
		return acc;
	}, []);
}

export {
	actionEditAssetSwap,
	determineAction,
	isApprovable,
	isDealerTradeBlocked,
	isRejectable,
	isConfirmable,
	isDeletable,
	renderString,
	getUnprocessedVins,
};
